import { useState } from 'react';

import { FileSearch, Search, Upload } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

import { ServiceHistoryInstructionsCard } from '@/components/service-history/ServiceHistoryInstructionsCard';
import { ServiceHistoryTokensCard } from '@/components/service-history/ServiceHistoryTokensCard';
import { ServicePdfDesignCard } from '@/components/service-history/ServicePdfDesignCard';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useServiceVehicles } from '@/hooks/useServiceHistory';
import { useAuthStore } from '@/stores/auth.store';

const PAGE_SIZE = 25;

/** Every vehicle with uploaded service history, searchable by registration or summary text. */
export const ServiceHistoryListPage = () => {
  usePageTitle('Service History');
  const navigate = useNavigate();
  const isAdmin = useAuthStore((state) => state.user?.role === 'admin');

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const { data, isLoading } = useServiceVehicles({
    search: search.trim() || undefined,
    page,
    limit: PAGE_SIZE,
  });

  if (!isAdmin) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-semibold">Service History</h1>
        <p className="text-muted-foreground mt-2 text-sm">
          Service history is restricted to administrators.
        </p>
      </div>
    );
  }

  const vehicles = data?.data ?? [];

  return (
    <div className="space-y-4 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <FileSearch className="h-6 w-6" />
          <div>
            <h1 className="text-2xl font-semibold">Service History</h1>
            <p className="text-muted-foreground text-sm">
              Service books, invoices and photos read by AI, saved per registration.
            </p>
          </div>
        </div>
        <Button asChild>
          <Link to="/service-history/upload">
            <Upload className="mr-2 h-4 w-4" />
            Upload files
          </Link>
        </Button>
      </div>

      <div className="relative max-w-md">
        <Search className="text-muted-foreground absolute top-1/2 left-2.5 h-4 w-4 -translate-y-1/2" />
        <Input
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(1);
          }}
          placeholder="Search registration, garage, work done…"
          className="pl-8"
        />
      </div>

      {isLoading && <p className="text-muted-foreground text-sm">Loading…</p>}
      {!isLoading && vehicles.length === 0 && (
        <p className="text-muted-foreground text-sm">
          {search ? 'No vehicles match this search.' : 'No service history uploaded yet.'}
        </p>
      )}

      {vehicles.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Registration</TableHead>
              <TableHead>Last service</TableHead>
              <TableHead>Services</TableHead>
              <TableHead>Files</TableHead>
              <TableHead>Model</TableHead>
              <TableHead>Generated</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {vehicles.map((vehicle) => (
              <TableRow
                key={vehicle.id}
                className="cursor-pointer"
                onClick={() => navigate(`/service-history/${vehicle.id}`)}
              >
                <TableCell>
                  <p className="font-mono text-sm font-medium">{vehicle.display_registration}</p>
                  {vehicle.match_snippet && (
                    <p className="text-muted-foreground mt-1 max-w-md text-xs">
                      {vehicle.match_snippet}
                    </p>
                  )}
                </TableCell>
                <TableCell className="text-sm">
                  {vehicle.last_service
                    ? `${vehicle.last_service.date}${
                        vehicle.last_service.mileage !== null
                          ? ` · ${vehicle.last_service.mileage.toLocaleString('en-GB')} mi`
                          : ''
                      }`
                    : '—'}
                </TableCell>
                <TableCell className="text-sm">{vehicle.total_services ?? '—'}</TableCell>
                <TableCell className="text-sm">{vehicle.document_count}</TableCell>
                <TableCell className="text-xs">
                  {vehicle.model_name ?? <Badge variant="secondary">No summary</Badge>}
                </TableCell>
                <TableCell className="text-muted-foreground text-xs">
                  {vehicle.generated_at
                    ? new Date(vehicle.generated_at).toLocaleString('en-GB')
                    : '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {data && data.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-muted-foreground text-sm">
            Page {data.page} of {data.totalPages} ({data.total} vehicles)
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= data.totalPages}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      <ServiceHistoryInstructionsCard />

      <ServicePdfDesignCard />

      <ServiceHistoryTokensCard />
    </div>
  );
};
